import { useId } from 'react'

import { FxAlert, FxButton, FxIcon } from '@/components/fx'
import { DELIVERED, armOptionLabel, armOptionName, type ArmOption } from './armOptions'

/**
 * Choosing which reading the table shows.
 *
 * The whole value of recognition arms is the comparison, which is worth
 * nothing if a person cannot tell at a glance whether they are looking at
 * what the product delivers or at an experiment. So: the delivered reading is
 * the default and is named as such, an arm on screen puts a standing notice
 * above the table with one click back, and the export follows whatever is
 * selected.
 */

export interface ArmPickerProps {
  options: ArmOption[]
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}

export function ArmPicker({ options, value, onChange, disabled }: ArmPickerProps) {
  const id = useId()
  // One stored reading is no choice: with only the delivered values there is
  // nothing to compare and a lone-option dropdown is furniture.
  if (options.length <= 1) return null

  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="text-small text-text-muted">
        Lesung
      </label>
      <select
        id={id}
        className="h-control rounded-md bg-surface px-3 text-body text-text shadow-raised"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {armOptionLabel(option)}
            {option.value !== DELIVERED && option.delivered ? ' (geliefert)' : ''}
          </option>
        ))}
      </select>
    </div>
  )
}

export interface ArmNoticeProps {
  option?: ArmOption
  /** How many of the rows on screen actually carry that reading. */
  coverage: { shown: number; total: number }
  /** The gateway returned no records at all for this arm. */
  empty: boolean
  onReset: () => void
}

/**
 * The standing notice while an arm is on screen.
 *
 * `empty` used to be the *expected* case for every arm a model ran: `GET
 * …/variant-records` matched only rows with an empty model column, so
 * `llm_text` and `llm_image` always came back with nothing. The gateway now
 * takes `&model=` and, without one, falls back to the newest reading per
 * document — so an empty list is no longer a known bug but a genuine
 * surprise, the picker having offered this reading only because the template
 * reports documents for it. It stays its own case rather than an error strip
 * because what the reader needs to know is unchanged: the table is showing
 * the delivered values.
 */
export function ArmNotice({ option, coverage, empty, onReset }: ArmNoticeProps) {
  if (!option || option.value === DELIVERED) return null

  return (
    <FxAlert tone={empty ? 'warning' : 'info'} className="mb-5">
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-2">
          <FxIcon name="auto_awesome" size={20} />
          {empty ? (
            <span>
              Für die Lesung „{armOptionName(option)}“ konnten keine Werte geladen werden. Die Tabelle zeigt
              weiterhin die gelieferten Werte.
            </span>
          ) : (
            <span>
              Sie sehen die Lesung „{armOptionName(option)}“ — nicht die gelieferten Werte. {coverage.shown}{' '}
              von {coverage.total} angezeigten Datensätzen wurden so gelesen; die übrigen zeigen
              ihre gelieferten Werte (mit * markiert). Filter und Zählung beziehen sich auf die
              gelieferten Werte.
            </span>
          )}
        </span>
        <FxButton variant="outlined" onClick={onReset}>
          Gelieferte Werte anzeigen
        </FxButton>
      </div>
    </FxAlert>
  )
}
